import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControlLabel, Switch,
} from '@mui/material';
import { IPieceRateOperation, IPieceRateOperationCreateRequest, IPieceRateOperationUpdateRequest } from '../../interfaces/pieceRate.interface';
import { toLocalIsoDate } from '../../utils/dateUtils';

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (data: IPieceRateOperationCreateRequest | IPieceRateOperationUpdateRequest) => void;
    operation: IPieceRateOperation | null;
}

/**
 * The piece-rate operation catalog, modelled on WageGradeForm.tsx. R2: `code` and `effectiveFrom`
 * are immutable after creation -- a rate change is a NEW row sharing the same code with a later
 * effectiveFrom, never an edit to this one (mirrors PieceRateOperationUpdateDTO, which has no
 * ratePerPiece/effectiveFrom field at all).
 */
const PieceRateOperationForm: React.FC<Props> = ({ open, onClose, onSave, operation }) => {
    const [code, setCode] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [ratePerPiece, setRatePerPiece] = useState<number>(0);
    const [effectiveFrom, setEffectiveFrom] = useState('');
    const [notes, setNotes] = useState('');
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        if (operation) {
            setCode(operation.code);
            setDisplayName(operation.displayName);
            setRatePerPiece(operation.ratePerPiece);
            setEffectiveFrom(operation.effectiveFrom);
            setNotes(operation.notes || '');
            setIsActive(operation.isActive);
        } else {
            setCode('');
            setDisplayName('');
            setRatePerPiece(0);
            setEffectiveFrom(toLocalIsoDate());
            setNotes('');
            setIsActive(true);
        }
    }, [operation, open]);

    const handleSubmit = () => {
        if (operation) {
            onSave({ displayName, isActive, notes: notes || undefined } as IPieceRateOperationUpdateRequest);
        } else {
            onSave({
                code, displayName, ratePerPiece, effectiveFrom, notes: notes || undefined,
            } as IPieceRateOperationCreateRequest);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'pieceRate.opForm.page' } as any}>
            <DialogTitle>{operation ? 'Edit' : 'Add'} Piece-Rate Operation</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus={!operation}
                    margin="dense"
                    label="Code" data-help="pieceRate.opForm.code"
                    fullWidth
                    value={code}
                    disabled={!!operation}
                    helperText={operation ? 'Code cannot be changed after creation' : 'e.g. SEWING_COLLAR'}
                    onChange={(e) => setCode(e.target.value)}
                    sx={{ mb: 2, mt: 1 }}
                />
                <TextField
                    margin="dense"
                    label="Display Name" data-help="pieceRate.opForm.displayName"
                    fullWidth
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    margin="dense"
                    label="Rate Per Piece" data-help="pieceRate.opForm.rate"
                    type="number"
                    fullWidth
                    value={ratePerPiece}
                    disabled={!!operation}
                    helperText={operation ? 'A rate change is a new row sharing this code with a later effective date' : undefined}
                    onChange={(e) => setRatePerPiece(parseFloat(e.target.value) || 0)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    margin="dense"
                    label="Effective From" data-help="pieceRate.opForm.effectiveFrom"
                    type="date"
                    fullWidth
                    value={effectiveFrom}
                    disabled={!!operation}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <TextField
                    margin="dense"
                    label="Notes" data-help="pieceRate.opForm.notes"
                    fullWidth
                    multiline
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={{ mb: 2 }}
                />
                {operation && (
                    <FormControlLabel
                        control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                        label="Active" data-help="pieceRate.opForm.active"
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-help="pieceRate.opForm.cancel">Cancel</Button>
                <Button onClick={handleSubmit} data-help="pieceRate.opForm.save" variant="contained"
                        disabled={!displayName.trim() || (!operation && (!code.trim() || ratePerPiece <= 0 || !effectiveFrom))}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PieceRateOperationForm;
